import { useApolloClient } from "@apollo/client";
import {
  WsmFitmentProductApplicationFragment,
  WsmFitmentProductApplicationsDocument,
  WsmFitmentProductApplicationsQuery,
  WsmFitmentProductApplicationsQueryVariables,
} from "@dashboard/graphql";
import React from "react";

import { chunk } from "./utils";

/** The per-call cap on `ids` (Saleor's connection page size). */
export const APPLICATION_IDS_PER_CALL = 100;

/** How many of those calls run at once. */
export const APPLICATION_CALLS_IN_FLIGHT = 4;

export interface SchemaApplicationsResult {
  /** The product's applications under the schema, by id. */
  byId: Map<string, WsmFitmentProductApplicationFragment>;
  /** True while the ids are being read back. */
  resolving: boolean;
  error: boolean;
  /** Reads back the ids that are still unresolved. */
  retry: () => void;
}

/** Runs `run` over `items`, at most `limit` at a time, in order of start. */
export const runPooled = async <T>(
  items: T[],
  limit: number,
  run: (item: T) => Promise<void>,
): Promise<void> => {
  let next = 0;
  const worker = async () => {
    while (next < items.length) {
      const item = items[next];

      next += 1;
      await run(item);
    }
  };

  await Promise.all(Array.from({ length: Math.min(limit, items.length) }, worker));
};

/**
 * Reads back the applications a product carries under one schema by their
 * ids, at most 100 a call and four calls at a time. An id already read back
 * (by this card, since it mounted) is not asked for again, so a save that
 * adds one application reads back one id, not the whole list. An id the
 * server does not return (a row from another schema, or one deleted
 * underneath us) stays in the product's list as a bare id and is kept
 * verbatim on save; it is not asked for again either.
 */
export const useSchemaApplications = (
  schemaId: string,
  wanted: string[],
): SchemaApplicationsResult => {
  const client = useApolloClient();
  // Per schema: what the server returned, and what it was asked for and did not return.
  const cache = React.useRef({
    schemaId,
    found: new Map<string, WsmFitmentProductApplicationFragment>(),
    absent: new Set<string>(),
  });
  const [version, setVersion] = React.useState(0);
  const [attempt, setAttempt] = React.useState(0);
  const [resolving, setResolving] = React.useState(false);
  const [error, setError] = React.useState(false);
  const wantedKey = wanted.join(",");

  if (cache.current.schemaId !== schemaId) {
    cache.current = { schemaId, found: new Map(), absent: new Set() };
  }

  React.useEffect(() => {
    let cancelled = false;
    const { found, absent } = cache.current;
    const missing = Array.from(new Set(wanted)).filter(id => !found.has(id) && !absent.has(id));

    setError(false);

    if (missing.length === 0) {
      // A superseded read may have filled the cache after its last render.
      setVersion(current => current + 1);
      setResolving(false);

      return;
    }

    setResolving(true);

    let failed = false;

    runPooled(chunk(missing, APPLICATION_IDS_PER_CALL), APPLICATION_CALLS_IN_FLIGHT, async ids => {
      if (cancelled) {
        return;
      }

      try {
        const result = await client.query<
          WsmFitmentProductApplicationsQuery,
          WsmFitmentProductApplicationsQueryVariables
        >({ query: WsmFitmentProductApplicationsDocument, variables: { schema: schemaId, ids } });
        const connection = result.data?.wsmFitmentApplications;

        if (!connection) {
          failed = true;

          return;
        }

        // Kept even if this read was superseded: the ids are still this schema's.
        connection.edges.forEach(edge => found.set(edge.node.id, edge.node));
        ids.filter(id => !found.has(id)).forEach(id => absent.add(id));
      } catch {
        failed = true;
      }
    }).then(() => {
      if (cancelled) {
        return;
      }

      setVersion(current => current + 1);
      setError(failed);
      setResolving(false);
    });

    return () => {
      cancelled = true;
    };
    // wantedKey stands in for the id list, which is rebuilt per render.
  }, [client, schemaId, wantedKey, attempt]);

  const byId = React.useMemo(() => {
    const map = new Map<string, WsmFitmentProductApplicationFragment>();

    wanted.forEach(id => {
      const application = cache.current.found.get(id);

      if (application) {
        map.set(id, application);
      }
    });

    return map;
  }, [schemaId, wantedKey, version]);
  const retry = React.useCallback(() => setAttempt(current => current + 1), []);

  return { byId, resolving, error, retry };
};
